import { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
    ArrowRightIcon,
    ArrowTopRightOnSquareIcon,
    PlusIcon,
    ShareIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import { TbCalendar } from "react-icons/tb";

import {
    EndpointAttachmentFileFormat,
    EndpointEmailBodyType,
    EndpointEmailIncludeInput,
    EndpointActionExecOn,
    IContextBase,
    IEndpointAction,
    IEndpointActionCondition,
    IEndpointActionEmail,
    IEndpointAttachment,
    EndpointActionConditionType,
    IEndpointOutputNameElement,
    IEndpointSplitByField,
    IOrgUser,
    IScrapesRenderConfig,
    ITemplateBase,
    IEndpointActionEmailDigest,
    IConnector,
    EndpointItemAggregation,
    IEndpointActionEndpoint,
    IEndpointBase,
    IContextInputPageFilters,
    ITemplate,
    IContextFieldDataType,
    IConnectorEmailDigest,
    IEndpointActionWebhook,
    IConnectorWebhook,
    EndpointWebhookIncludeInput,
    EndpointActionStatus,
    IConnectorWebhookDetails,
    IConnectorEmailDigestDetails,
    IEndpointActionTableUpdate,
    ILookupTableBase,
    TableUpdateActionType,
    IScrapesRenderColumnConfig,
    ITableUpdateColumnConfig
} from "../lib/backend/extractions.types.generated";
import {
    USER_ROLES
} from "../lib/consts";
import {
    deepCopy,
    redirectToExternalPage,
} from "../lib/utils";
import { selectUser } from "../lib/scraper.slice";

import { IEndpointSplitByFieldEx } from "../screens/NewEndpoint";
import { WarningModal } from "./WarningModal";
import {
    AttachmentHandlebarDetails,
    AttachmentJavascriptDetails,
    ICustomJavascriptDetails,
    IHandlebarDetails
} from "./CustomAttachmentDetails";
import { Dropdown } from "./Dropdown";
import { Checkbox } from "./Checkbox";
import { EndpointOutputName } from "./EndpointOutputName";
import {
    Button,
    IconButton
} from "./Button";
import {
    EndpointCustomOutput,
    EndpointCustomOutputSheets,
    EndpointSplitByField
} from "./EndpointCustomOutput";
import { SidePanel } from "./SidePanel";
import {
    CompactTabs,
    ITab
} from "./Tabs";
import {
    AdminIntegerInput,
    Textbox
} from "./Textbox";
import { PageFilters } from "./PageFilters";
import { BackendObj } from "../lib/backend";
import { LoadingSpinnerLimit } from "./LoadingSpinner";
import { Toggle } from "./Toggle";
import {
    TextboxModal,
    TwoTextboxModal
} from "./TextboxModal";
import { getInitConnectorDetails } from "../screens/NewConnector";
import { EndpointOutputColumns } from "./EndpointOutputColumns";

export const INIT_ATTACHMENTS: IEndpointAttachment[] = [{
    file_format: "excel",
    handlebar_template: "",
    handlebar_file_extension: "",
    handlebar_file_mimetype: "",
    custom_javascript_file_format: "json",
    custom_javascript_formula: "({})",
    custom_file_name: [],
    custom_output_sheets: []
}];

const INIT_CONDITIONS: Record<EndpointActionConditionType, IEndpointActionCondition> = {
    "skip_on_empty_results": {
        type: "skip_on_empty_results"
    },
    "field_regex": {
        type: "field_regex",
        field_uuid: "",
        regex: ""
    }
}

export const INIT_EMAIL_ACTION: IEndpointActionEmail = {
    status: "active",
    exec_on: "process",
    type: "email",
    conditions: [],
    item_aggregation: "per_job",
    to_email_addresses: [],
    from_email_address: undefined,
    reply_to_sender: true,
    email_subject: [],
    email_body: "result_data",
    custom_email_body: undefined,
    attachments: deepCopy(INIT_ATTACHMENTS),
    include_input: "attachments"
};

export const INIT_EMAIL_DIGEST_ACTION: IEndpointActionEmailDigest = {
    status: "active",
    exec_on: "process",
    type: "email_digest",
    item_aggregation: "per_item",
    conditions: [],
    connector_uuid: "",
    custom_output_sheets: []
};

export const INIT_ENDPOINT_ACTION: IEndpointActionEndpoint = {
    status: "active",
    exec_on: "process",
    type: "endpoint",
    item_aggregation: "per_item",
    conditions: [],
    endpoint_uuid: "",
    page_filters: { filters: [], invert: false },
    max_chain_length: 0,
    max_target_revisit: 0,
    field_overrides: []
};

export const INIT_WEBHOOK_ACTION: IEndpointActionWebhook = {
    status: "active",
    exec_on: "process",
    type: "webhook",
    item_aggregation: "per_item",
    conditions: [],
    connector_uuid: "",
    custom_output_sheets: [],
    include_input: "no",
    attachments: []
}

export const INIT_TABLE_UPDATE_ACTION: IEndpointActionTableUpdate = {
    status: "active",
    exec_on: "process",
    type: "table_update",
    item_aggregation: "per_item",
    conditions: [],
    lookup_table_uuid: "",
    update_type: "append",
    mapping: {
        name: "",
        context_uuid: "",
        columns: []
    }
}

export function getActionType(action: IEndpointAction) {
    switch (action.type) {
        case "email":
            return "Send email";
        case "email_digest":
            return "Include in digest email";
        case "endpoint":
            return "Trigger workflow";
        case "webhook":
            return "Call webhook";
        case "table_update":
            return "Update table";
        default:
            return "Unknown";
    }
}

export function getActionWhen(action: IEndpointAction) {
    switch (action.exec_on) {
        case "process":
            return "Processing complete";
        case "confirm":
            return "Result confirmed";
        case "reject":
            return "Result rejected";
        default:
            return "Unknown";
    }
}

function getElementDisplayValue(element: IEndpointOutputNameElement, contexts: IContextBase[]): string {
    if (element.type === "constant") return element.constant;
    if (element.type === "field") {
        const context = contexts.find(c => c.uuid === element.context_uuid);
        const field = context?.fields.find(f => f.uuid === element.field_uuid);
        return field ? `[${field.name}]` : "[field]";
    }
    return "";
}

export function getActionDescription(
    action: IEndpointAction,
    connectors: IConnector[],
    endpoints: IEndpointBase[],
    lookup_tables: ILookupTableBase[],
    contexts: IContextBase[]
) {
    switch (action.type) {
        case "email": {
            let recipients: string[] = action.to_email_addresses.map(addr => getElementDisplayValue(addr, contexts));
            if (action.reply_to_sender) { recipients.push("sender"); }
            let attachments = action.attachments.map((attachment) => attachment.file_format);
            return `To ${recipients.join(", ")}; Attachments: ${attachments.length > 0 ? attachments.join(", ") : "/"}`;
        }
        case "email_digest": {
            const connector = connectors.find((connector) => connector.uuid === action.connector_uuid) as IConnectorEmailDigest;
            return `Connector: ${connector?.name ?? "Unknown"}; File format: ${connector?.details?.file_format ?? "Unknown"}`;
        }
        case "endpoint": {
            const endpoint = endpoints.find((endpoint) => endpoint.uuid === action.endpoint_uuid);
            return `Workflow: ${endpoint?.name ?? "Unknown"}`;
        }
        case "webhook": {
            const connector = connectors.find((connector) => connector.uuid === action.connector_uuid) as IConnectorWebhook;
            let attachments = action.attachments.map((attachment) => attachment.file_format);
            return `Connector: ${connector?.name ?? "Unknown"}; Attachments: ${attachments.length > 0 ? attachments.join(", ") : "/"}`;
        }
        case "table_update": {
            const lookup_table = lookup_tables.find((table) => table.uuid === action.lookup_table_uuid);
            return `Table: ${lookup_table?.name ?? "Unknown"}`;
        }
        default:
            return "Unknown";
    }
}

interface IObjectField {
    display_name: string;
    uuid: string;
    datatype: IContextFieldDataType;
}

function getObjectFields(contexts: IContextBase[]): IObjectField[] {
    return contexts.filter(ctx => ctx.type === "object").flatMap(ctx => ctx.fields.map(field => ({
        display_name: `[${ctx.name}] ${field.name}`,
        uuid: field.uuid,
        datatype: field.datatype
    })));
}

type ActionProps = {
    org_users: IOrgUser[];
    template: ITemplateBase;
    contexts: IContextBase[];
    connectors: IConnector[];
    endpoints: IEndpointBase[];
    lookup_tables: ILookupTableBase[];
    action: IEndpointAction;
    refreshConnectors(): void;
    onClose(updated_action?: IEndpointAction): void;
}

export function EditAction({ org_users, template, contexts, connectors, endpoints, lookup_tables, action, refreshConnectors, onClose }: ActionProps) {
    switch (action.type) {
        case "email":
            return <EditActionEmail
                org_users={org_users}
                template={template}
                contexts={contexts}
                action={action}
                onClose={onClose}
            />;
        case "email_digest":
            return <EditActionEmailDigest
                template={template}
                contexts={contexts}
                connectors={connectors}
                action={action}
                refreshConnectors={refreshConnectors}
                onClose={onClose}
            />;
        case "endpoint":
            return <EditActionEndpoint
                contexts={contexts}
                endpoints={endpoints}
                action={action}
                onClose={onClose}
            />;
        case "webhook":
            return <EditActionWebhook
                template={template}
                contexts={contexts}
                connectors={connectors}
                action={action}
                refreshConnectors={refreshConnectors}
                onClose={onClose}
            />;
        case "table_update":
            return <EditActionTableUpdate
                contexts={contexts}
                lookup_tables={lookup_tables}
                action={action}
                onClose={onClose}
            />;
        default:
            return null;
    }
}

type ActionHeaderProps = {
    contexts: IContextBase[];
    status: EndpointActionStatus;
    setStatus(status: EndpointActionStatus): void;
    exec_on: EndpointActionExecOn;
    setExecOn(exec_on: EndpointActionExecOn): void;
    conditions: IEndpointActionCondition[];
    setConditions(conditions: IEndpointActionCondition[]): void;
}

function ActionHeader({ contexts, status, setStatus, exec_on, setExecOn, conditions, setConditions }: ActionHeaderProps) {
    const string_fields: IObjectField[] = useMemo<IObjectField[]>(() =>
        getObjectFields(contexts).filter(field => ["enum", "string"].includes(field.datatype)),
        [contexts]);

    const addCondition = () => {
        setConditions([...conditions, deepCopy(INIT_CONDITIONS["skip_on_empty_results"])]);
    }

    const deleteCondition = (condition_idx: number) => {
        setConditions(conditions.filter((_, idx) => idx !== condition_idx));
    }

    const updateConditionType = (condition_idx: number, type: EndpointActionConditionType) => {
        // when changing the type, we need to reset the condition
        setConditions(conditions.map((condition, idx) => idx === condition_idx ? { ...INIT_CONDITIONS[type] } : condition));
    }

    const updateConditionFieldRegex = (condition_idx: number, key: "field_uuid" | "regex", value: string) => {
        setConditions(conditions.map((condition, idx) => idx === condition_idx ? { ...condition, [key]: value } : condition));
    }

    return <div className="sm:grid sm:grid-cols-10 sm:items-center sm:gap-4">
        <div className="flex flex-row items-center gap-x-2 sm:col-span-2">
            <p className="text-sm font-medium leading-6 text-gray-900">Execute on</p>
        </div>
        <div className="sm:col-span-7 flex flex-row items-center gap-x-2 w-full">
            <Dropdown
                values={["Processing complete", "Confirmed", "Rejected"]}
                ids={["process", "confirm", "reject"]}
                selected={exec_on}
                onChange={(exec_on) => setExecOn(exec_on as EndpointActionExecOn)}
            />
        </div>
        <div className="flex flex-col items-end gap-y-1">
            <Toggle
                enabled={status === "active"}
                setEnabled={(status) => setStatus(status ? "active" : "disabled")}
                toggle_color={status === "active" ? "bg-mint-500" : "bg-torch_red-500"}
            />
        </div>
        <div className="sm:col-span-2 flex flex-row items-center gap-x-2 pt-4">
            <p className="text-sm font-medium leading-6 text-gray-900">Conditions</p>
        </div>
        <div className="sm:col-span-8 flex flex-row items-center gap-x-2 w-full pt-4">
            {conditions.length === 0 && <p className="text-xs text-gray-500 grow text-center">
                No conditions - action will be executed every time the job is executed.
            </p>}
            {conditions.length > 0 && <p className="grow"></p>}
            <Button icon={PlusIcon} onClick={addCondition} />
        </div>
        {conditions.map((condition, idx) => (
            <div key={idx} className="sm:col-span-10 sm:grid sm:grid-cols-5 sm:gap-4 sm:items-center p-4 border border-gray-900/10 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium leading-6 text-gray-900">Condition {idx + 1}</p>
                <div className="sm:col-span-4 flex flex-row items-center gap-x-2">
                    <Dropdown
                        values={["Skip on empty results", "Field regex"]}
                        ids={["skip_on_empty_results", "field_regex"]}
                        selected={condition.type}
                        onChange={(type) => updateConditionType(idx, type as EndpointActionConditionType)} />
                    <IconButton icon={XMarkIcon} onClick={() => deleteCondition(idx)} />
                </div>
                {condition.type === "field_regex" && <Fragment>
                    <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                        <p>Field for regex</p>
                    </label>
                    <div className="sm:col-span-3">
                        <Dropdown
                            values={string_fields.map((field) => field.display_name)}
                            ids={string_fields.map((field) => field.uuid)}
                            selected={condition.field_uuid}
                            placeholder="Select a field"
                            onChange={(field_uuid) => updateConditionFieldRegex(idx, "field_uuid", field_uuid)}
                        />
                    </div>
                    <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                        <p>Regex pattern</p>
                    </label>
                    <div className="sm:col-span-3">
                        <Textbox
                            value={condition.regex}
                            onChange={(regex) => updateConditionFieldRegex(idx, "regex", regex)}
                            placeholder="Enter a regex pattern"
                        />
                    </div>
                </Fragment>}
            </div>
        ))}
    </div>;
}

type EditActionEmailProps = {
    org_users: IOrgUser[];
    template: ITemplateBase;
    contexts: IContextBase[];
    action: IEndpointActionEmail;
    onClose(updated_action?: IEndpointActionEmail): void;
}

function EditActionEmail({ org_users, template, contexts, action: init_action, onClose }: EditActionEmailProps) {
    const user = useSelector(selectUser);

    const [is_changed, setIsChanged] = useState<boolean>(false);
    const [action, setAction] = useState<IEndpointActionEmail>(init_action);
    const [are_custom_formats_not_available_open, setAreCustomFormatsNotAvailableOpen] = useState<boolean>(false);
    const [attachment_custom_output_idx, setAttachmentCustomOutputIdx] = useState<number | undefined>(undefined);
    const [attachment_editor_idx, setAttachmentEditorIdx] = useState<number | undefined>(undefined);
    const [selected_tab, setSelectedTab] = useState<string>("email_body");

    const tabs: ITab[] = [
        { name: "Email", key: "email_body" },
        { name: "Advanced", key: "advanced_settings" }
    ];

    const are_custom_attachment_formats_enabled = useMemo<boolean>(
        () => (user.role === USER_ROLES.admin || user.role === USER_ROLES.enterprise),
        [user.role]);
    const split_fields_data = useMemo<IEndpointSplitByFieldEx[]>(
        () => {
            const new_split_fields_data: IEndpointSplitByFieldEx[] = [];
            for (const context of contexts || []) {
                const split = action.split_by_field?.find((split) => split.context_uuid === context.uuid);
                if (split === undefined) { continue; }
                const field = context.fields.find((field) => field.uuid === split.field_uuid);
                if (field === undefined) { continue; }
                new_split_fields_data.push({
                    context_uuid: context.uuid,
                    context,
                    field_uuid: field.uuid,
                    field_name: field.name,
                    required: split.required ?? false,
                    context_type: split.context_type
                });
            }
            return new_split_fields_data;
        }, [contexts, action.split_by_field]);

    const handleClose = () => {
        onClose(is_changed ? action : undefined);
    }

    const updateStatus = (status: EndpointActionStatus) => {
        setAction((prev_action) => ({ ...prev_action, status }));
        setIsChanged(true);
    }

    const updateExecOn = (exec_on: EndpointActionExecOn) => {
        setAction((prev_action) => ({ ...prev_action, exec_on }));
        setIsChanged(true);
    }

    const updateConditions = (conditions: IEndpointActionCondition[]) => {
        setAction((prev_action) => ({ ...prev_action, conditions }));
        setIsChanged(true);
    }

    const updateSplitByField = (split_by_field: IEndpointSplitByField[] | undefined) => {
        setAction((prev_action) => ({ ...prev_action, split_by_field }));
        setIsChanged(true);
    }

    const addToEmailAddressElement = (element: IEndpointOutputNameElement) => {
        setIsChanged(true);
        setAction((prev_action) => ({
            ...prev_action,
            to_email_addresses: [...prev_action.to_email_addresses, element]
        }));
    }

    const removeToEmailAddressElement = (element_idx: number) => {
        setIsChanged(true);
        setAction((prev_action) => ({
            ...prev_action,
            to_email_addresses: prev_action.to_email_addresses.filter((_, idx) => idx !== element_idx)
        }));
    }

    const addFromEmailAddressElement = (element: IEndpointOutputNameElement) => {
        setIsChanged(true);
        setAction((prev_action) => ({
            ...prev_action,
            from_email_address: element
        }));
    }

    const removeFromEmailAddressElement = () => {
        setIsChanged(true);
        setAction((prev_action) => ({
            ...prev_action,
            from_email_address: undefined
        }));
    }

    const toggleReplyToSender = (reply_to_sender: boolean) => {
        setAction((prev_action) => ({ ...prev_action, reply_to_sender }));
        setIsChanged(true);
    }

    const addEmailSubjectElement = (element: IEndpointOutputNameElement) => {
        const new_email_subject = [...(action.email_subject || []), { ...element }];
        setAction((prev_action) => ({ ...prev_action, email_subject: new_email_subject }));
        setIsChanged(true);
    }

    const removeEmailSubjectElement = (element_idx: number) => {
        const new_email_subject = [...(action.email_subject || [])];
        new_email_subject.splice(element_idx, 1);
        setAction((prev_action) => ({ ...prev_action, email_subject: new_email_subject }));
        setIsChanged(true);
    }

    const updateEmailBody = (email_body: EndpointEmailBodyType) => {
        setAction((prev_action) => ({
            ...prev_action,
            email_body,
            custom_email_body: email_body === "custom" ? prev_action.custom_email_body : undefined
        }));
        setIsChanged(true);
    }

    const addCustomEmailBodyElement = (element: IEndpointOutputNameElement) => {
        setAction((prev_action) => ({
            ...prev_action,
            custom_email_body: [...(prev_action.custom_email_body || []), element]
        }));
        setIsChanged(true);
    }

    const removeCustomEmailBodyElement = (element_idx: number) => {
        setIsChanged(true);
        setAction((prev_action) => {
            const new_custom_email_body = prev_action.custom_email_body?.filter((_, idx) => idx !== element_idx) || [];
            return {
                ...prev_action,
                custom_email_body: new_custom_email_body.length === 0 ? undefined : new_custom_email_body
            };
        });
    }

    const deleteAttachment = (idx: number) => {
        const new_attachments = [...action.attachments];
        new_attachments.splice(idx, 1);
        setAction((prev_action) => ({ ...prev_action, attachments: new_attachments }));
        setIsChanged(true);
    }

    const addAttachment = () => {
        const new_attachments = [...action.attachments, deepCopy(INIT_ATTACHMENTS[0])];
        setAction((prev_action) => ({ ...prev_action, attachments: new_attachments }));
        setIsChanged(true);
    }

    const updateAttachmentFileFormat = (attachment_idx: number, format: EndpointAttachmentFileFormat) => {
        if ((format === "custom" || format === "custom_javascript") && !are_custom_attachment_formats_enabled) {
            setAreCustomFormatsNotAvailableOpen(true);
        } else {
            setAction((prev_action) => ({
                ...prev_action,
                attachments: prev_action.attachments.map((a, idx) => attachment_idx === idx ? { ...a, file_format: format } : a)
            }));
        }
        setIsChanged(true);
    }

    const updateAttachmentCustomFileName = (attachment_idx: number, custom_file_name: IEndpointOutputNameElement[]) => {
        setAction((prev_action) => ({
            ...prev_action,
            attachments: prev_action.attachments.map((a, idx) => attachment_idx === idx ? { ...a, custom_file_name } : a)
        }));
        setIsChanged(true);
    }

    const updateAttachmentCustomOutputSheets = (attachment_idx: number, custom_output_sheets: IScrapesRenderConfig[]) => {
        setAction((prev_action) => ({
            ...prev_action,
            attachments: prev_action.attachments.map((a, idx) => attachment_idx === idx ? { ...a, custom_output_sheets } : a)
        }));
        setIsChanged(true);
    }

    const updateAttachmentSplitByField = (attachment_idx: number, split_by_field: IEndpointSplitByField[] | undefined) => {
        const new_split_by_field = (split_by_field === undefined || split_by_field.length === 0) ? undefined : split_by_field;
        setAction((prev_action) => ({
            ...prev_action,
            attachments: prev_action.attachments.map((a, idx) => attachment_idx === idx ? { ...a, split_by_field: new_split_by_field } : a)
        }));
        setIsChanged(true);
    }

    const updateAttachmentHandlebarDetails = (attachment_idx: number, handlebar_details: IHandlebarDetails) => {
        setAction((prev_action) => ({
            ...prev_action,
            attachments: prev_action.attachments.map((a, idx) => attachment_idx === idx ? { ...a, ...handlebar_details } : a)
        }));
        setIsChanged(true);
        setAttachmentEditorIdx(undefined);
    }

    const updateAttachmentJavascriptDetails = (attachment_idx: number, javascript_details: ICustomJavascriptDetails) => {
        setAction((prev_action) => ({
            ...prev_action,
            attachments: prev_action.attachments.map((a, idx) => attachment_idx === idx ? { ...a, ...javascript_details } : a)
        }));
        setIsChanged(true);
        setAttachmentEditorIdx(undefined);
    }

    const updateIncludeInput = (include_input: EndpointEmailIncludeInput) => {
        setAction((prev_action) => ({ ...prev_action, include_input }));
        setIsChanged(true);
    }

    const updateItemAggregation = (item_aggregation: EndpointItemAggregation) => {
        setAction((prev_action) => ({ ...prev_action, item_aggregation }));
        setIsChanged(true);
    }

    return <SidePanel title="Action: Send email" open={true} size="2xl" onClose={handleClose}>
        <div className="py-4 flex flex-col gap-y-8">
            <ActionHeader
                contexts={contexts}
                status={action.status}
                setStatus={updateStatus}
                exec_on={action.exec_on}
                setExecOn={updateExecOn}
                conditions={action.conditions}
                setConditions={updateConditions}
            />

            <CompactTabs tabs={tabs} selected_tab_key={selected_tab} setSelectedTab={setSelectedTab} />

            {selected_tab === "email_body" && <Fragment>
                <div className="flex flex-col gap-y-4">
                    <div className="pb-4 sm:grid sm:grid-cols-5 sm:items-start sm:gap-4">
                        <label htmlFor="endpoint_forward_email_address" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-1">
                            <p>Recipients</p>
                        </label>
                        <div className="sm:col-span-4 sm:mt-0 flex flex-col gap-y-4 w-full">
                            <EndpointOutputName
                                title="To email addresses"
                                contexts={contexts}
                                split_fields_data={[]}
                                elements={action.to_email_addresses}
                                placeholder="Add email recipients"
                                constant_suggestions={org_users.map(user => user.email)}
                                addElement={addToEmailAddressElement}
                                removeElement={removeToEmailAddressElement}
                                separator="space"
                            />
                            <div className="flex flex-col gap-y-4 w-full">
                                <div className="py-1 flex flex-row items-center gap-2">
                                    <Checkbox
                                        id="endpoint_reply_to_sender"
                                        checked={action.reply_to_sender}
                                        setChecked={toggleReplyToSender}
                                    />
                                    <label htmlFor="endpoint_reply_to_sender" className="text-sm text-gray-600">
                                        Reply to the sender
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pb-4 sm:grid sm:grid-cols-5 sm:items-start sm:gap-4">
                        <label htmlFor="endpoint_forward_email_address" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-1">
                            <p>Sender</p>
                        </label>
                        <div className="sm:col-span-4 sm:mt-0 flex flex-col gap-y-4 w-full">
                            <EndpointOutputName
                                title="From email address (only custom message)"
                                contexts={contexts}
                                split_fields_data={[]}
                                elements={action.from_email_address ? [action.from_email_address] : []}
                                placeholder="Use default 'from' address"
                                constant_suggestions={org_users.map(user => user.email)}
                                addElement={addFromEmailAddressElement}
                                removeElement={removeFromEmailAddressElement}
                                separator="space"
                            />
                        </div>
                    </div>

                    <div className="mt-2 sm:mt-0 sm:col-span-3">
                        <EndpointOutputName
                            title={"Subject"}
                            contexts={contexts}
                            split_fields_data={split_fields_data}
                            elements={action.email_subject}
                            placeholder={`Default subject is "Nordoon: <input subject>"`}
                            single_line={true}
                            separator="space"
                            addElement={addEmailSubjectElement}
                            removeElement={removeEmailSubjectElement} />
                    </div>
                </div>

                <div className="flex flex-col gap-4 border-b border-gray-900/10 pb-8">
                    <div className="sm:col-span-6 flex flex-row items-center gap-x-2">
                        <p className="text-sm font-medium leading-6 sm:pt-1.5 text-gray-900">Attachments</p>
                        <div className="flex-grow" />
                        <Button icon={PlusIcon} onClick={addAttachment} />
                    </div>
                    {action.attachments.map((attachment, idx) => (
                        <div key={idx} className="sm:grid sm:grid-cols-6 sm:gap-4 p-4 border border-gray-900/10 bg-gray-50 rounded-lg">
                            <div className="sm:col-span-6 flex flex-row items-center gap-x-2">
                                <p className="text-sm font-medium leading-6 sm:pt-1.5 text-gray-900">Attachment {idx + 1}</p>
                                {attachment.custom_output_sheets.length > 0 && <p className="pt-1 text-xs text-gray-400">custom template</p>}
                                {attachment.custom_file_name.length > 0 && <p className="pt-1 text-xs text-gray-400">custom filename</p>}
                                {(attachment.split_by_field?.length ?? 0) > 0 && <p className="pt-1 text-xs text-gray-400">split enabled</p>}
                                <div className="flex-grow" />
                                <Button text={"Edit Format"} onClick={() => setAttachmentCustomOutputIdx(idx)} />
                                {attachment.file_format === "custom" && <Button text="Edit Handlebars" onClick={() => setAttachmentEditorIdx(idx)} />}
                                {attachment.file_format === "custom_javascript" && <Button text="Edit JavaScript" onClick={() => setAttachmentEditorIdx(idx)} />}
                                <IconButton icon={XMarkIcon} onClick={() => deleteAttachment(idx)} />
                            </div>
                            <label htmlFor={`endpoint_email_attachment_${idx}`}
                                className="block sm:col-span-3 text-sm font-medium leading-6 sm:pt-1.5 text-gray-900">
                                <p>Email attachment format</p>
                            </label>
                            <div className="mt-2 sm:mt-0 sm:col-span-3">
                                <Dropdown
                                    values={["Excel", "CSV (comma)", "CSV (semicolon)", "TSV", "PDF", "Prepend PDF to attachment", "Append PDF to attachment", "JSON", "Custom - Handlebars", "Custom - JavaScript"]}
                                    ids={["excel", "csv", "csv-semicolon", "tsv", "pdf", "prepend_pdf", "append_pdf", "json", "custom", "custom_javascript"]}
                                    selected={attachment.file_format}
                                    onChange={(format) => updateAttachmentFileFormat(idx, format as EndpointAttachmentFileFormat)} />
                            </div>
                        </div>
                    ))}
                    {attachment_custom_output_idx !== undefined && <Fragment>
                        <EndpointCustomOutput
                            title={`Attachment ${attachment_custom_output_idx + 1}`}
                            contexts={contexts}
                            custom_file_name={action.attachments[attachment_custom_output_idx].custom_file_name}
                            custom_output_sheets={["custom", "custom_javascript"].includes(action.attachments[attachment_custom_output_idx].file_format) ?
                                undefined : action.attachments[attachment_custom_output_idx].custom_output_sheets}
                            split_by_field={action.attachments[attachment_custom_output_idx].split_by_field}
                            is_committing={false}
                            updateCustomFileName={(custom_file_name) => updateAttachmentCustomFileName(attachment_custom_output_idx, custom_file_name)}
                            updateOutputSheets={["custom", "custom_javascript"].includes(action.attachments[attachment_custom_output_idx].file_format) ?
                                undefined : (custom_output_sheets) => updateAttachmentCustomOutputSheets(attachment_custom_output_idx, custom_output_sheets)}
                            updateSplitByField={(split_by_field) => updateAttachmentSplitByField(attachment_custom_output_idx, split_by_field)}
                            onClose={() => setAttachmentCustomOutputIdx(undefined)}
                        />
                    </Fragment>}
                    {attachment_editor_idx !== undefined && <Fragment>
                        {action.attachments[attachment_editor_idx].file_format === "custom" && <AttachmentHandlebarDetails
                            template_uuid={template.uuid}
                            split_by_field={action.attachments[attachment_editor_idx].split_by_field}
                            handlebar_template={action.attachments[attachment_editor_idx].handlebar_template}
                            handlebar_file_extension={action.attachments[attachment_editor_idx].handlebar_file_extension}
                            handlebar_file_mimetype={action.attachments[attachment_editor_idx].handlebar_file_mimetype}
                            onClose={(handlebar_details) => updateAttachmentHandlebarDetails(attachment_editor_idx, handlebar_details)}
                        />}
                        {action.attachments[attachment_editor_idx].file_format === "custom_javascript" && <AttachmentJavascriptDetails
                            template_uuid={template.uuid}
                            contexts={contexts}
                            split_by_field={action.attachments[attachment_editor_idx].split_by_field}
                            custom_javascript_file_format={action.attachments[attachment_editor_idx].custom_javascript_file_format}
                            custom_javascript_formula={action.attachments[attachment_editor_idx].custom_javascript_formula}
                            onClose={(javascript_details) => updateAttachmentJavascriptDetails(attachment_editor_idx, javascript_details)}
                        />}
                    </Fragment>}
                </div>
            </Fragment>}

            {selected_tab === "advanced_settings" && <Fragment>
                <div className="sm:grid sm:grid-cols-5 sm:items-start sm:gap-4">
                    <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                        <p>Include from input</p>
                    </label>
                    <div className="mt-2 sm:mt-0 col-span-3">
                        <Dropdown
                            values={["Nothing", "Attachments only", "First attachment only", "Email body and all attachments"]}
                            ids={["no", "attachments", "main_attachment", "all"]}
                            selected={action.include_input}
                            onChange={(include_input) => updateIncludeInput(include_input as EndpointEmailIncludeInput)} />
                    </div>
                </div>

                <div className="sm:grid sm:grid-cols-5 sm:items-start sm:gap-4">
                    <label htmlFor="endpoint_forward_email_address" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                        <p>Content</p>
                    </label>
                    <div className="mt-2 sm:col-span-3 sm:mt-0">
                        <div className="flex flex-col gap-y-4 w-full">
                            <Dropdown
                                values={["Response with result data", "Passthrough original email", "Custom message"]}
                                ids={["result_data", "passthrough", "custom"]}
                                selected={action.email_body}
                                onChange={(email_body) => updateEmailBody(email_body as EndpointEmailBodyType)} />
                            {action.email_body === "custom" && (
                                <EndpointOutputName
                                    title="Custom email body"
                                    contexts={contexts}
                                    split_fields_data={[]}
                                    elements={action.custom_email_body || []}
                                    placeholder="Enter custom email body content"
                                    addElement={addCustomEmailBodyElement}
                                    removeElement={removeCustomEmailBodyElement}
                                    separator="space"
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="sm:grid sm:grid-cols-5 sm:items-start sm:gap-4">
                    <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                        <p>Aggregation</p>
                    </label>
                    <div className="mt-2 sm:mt-0 w-full sm:col-span-3">
                        <Dropdown
                            values={["Per job", "Per item"]}
                            ids={["per_job", "per_item"]}
                            selected={action.item_aggregation}
                            onChange={(item_aggregation) => updateItemAggregation(item_aggregation as EndpointItemAggregation)} />
                    </div>
                </div>

                <div className="border-b border-gray-900/10 pb-8">
                    <EndpointSplitByField
                        prefix="email"
                        in_a_box={false}
                        contexts={contexts}
                        split_by_field={action.split_by_field}
                        is_committing={false}
                        tooltip="You can split the result into multiple individual emails by selecting one or more
                                split fields. A separate email is created for each unique combination of the split fields.
                                Steps for which no split fields are selected are included in all the emails."
                        updateSplitByField={(split_by_field) => updateSplitByField(split_by_field)} />

                </div>
            </Fragment>}

            <div className="flex flex-row justify-end max-w-4xl">
                <Button text="Close" highlight={true} onClick={handleClose} />
            </div>
        </div>

        <WarningModal
            title="Feature not available"
            open={are_custom_formats_not_available_open}
            onClose={() => setAreCustomFormatsNotAvailableOpen(false)}>
            <div className="flex flex-col text-sm text-gray-900 gap-y-4 py-4">
                <p>Custom attachment format are only enabled for our enterprise customers.</p>
                <p>Please <a href="https://meetings-eu1.hubspot.com/tomaz/book-a-demo" className="text-space_blue-600" target="_blank" rel="noreferrer">contact our team</a> for more information on how to upgrade.</p>
            </div>
        </WarningModal>
    </SidePanel >;
}

type EditActionEmailDigestProps = {
    template: ITemplateBase;
    contexts: IContextBase[];
    connectors: IConnector[];
    action: IEndpointActionEmailDigest;
    refreshConnectors(): void;
    onClose(updated_action?: IEndpointActionEmailDigest): void;
}

function EditActionEmailDigest({ template, contexts, connectors, action: init_action, refreshConnectors, onClose }: EditActionEmailDigestProps) {
    const [is_changed, setIsChanged] = useState<boolean>(false);
    const [action, setAction] = useState<IEndpointActionEmailDigest>(init_action);
    const [createEmailDigestOpen, setCreateEmailDigestOpen] = useState<boolean>(false);

    const email_digest_connectors = useMemo<IConnector[]>(
        () => connectors.filter((connector) => connector.type === "email_digest"),
        [connectors]);

    const handleClose = () => {
        onClose(is_changed ? action : undefined);
    }

    const updateStatus = (status: EndpointActionStatus) => {
        setAction((prev_action) => ({ ...prev_action, status }));
        setIsChanged(true);
    }

    const updateExecOn = (exec_on: EndpointActionExecOn) => {
        setAction((prev_action) => ({ ...prev_action, exec_on }));
        setIsChanged(true);
    }

    const updateConditions = (conditions: IEndpointActionCondition[]) => {
        setAction((prev_action) => ({ ...prev_action, conditions }));
        setIsChanged(true);
    }

    const updateConnectorUuid = (connector_uuid: string) => {
        setAction((prev_action) => ({ ...prev_action, connector_uuid }));
        setIsChanged(true);
    }

    const updateCustomOutputSheets = (custom_output_sheets: IScrapesRenderConfig[]) => {
        setAction((prev_action) => ({ ...prev_action, custom_output_sheets }));
        setIsChanged(true);
    }

    const createEmailDigestConnector = async (result: boolean, name?: string) => {
        setCreateEmailDigestOpen(false);
        if (result && name !== undefined) {
            const new_connector_details = getInitConnectorDetails("email_digest") as IConnectorEmailDigestDetails;
            const { connector_uuid } = await BackendObj.extractions.createConnector({
                name: name,
                type: "email_digest",
                org_uuid: template.org_uuid,
                details: new_connector_details
            });
            updateConnectorUuid(connector_uuid);
            refreshConnectors();
        }
    }

    return <SidePanel title="Action: Include in digest email" open={true} size="2xl" onClose={handleClose}>
        <div className="py-4 flex flex-col gap-y-8">
            <ActionHeader
                contexts={contexts}
                status={action.status}
                setStatus={updateStatus}
                exec_on={action.exec_on}
                setExecOn={updateExecOn}
                conditions={action.conditions}
                setConditions={updateConditions}
            />

            <div className="flex flex-col gap-y-4 border-b border-gray-900/10 pb-4">
                <div className="pb-4 sm:grid sm:grid-cols-5 sm:items-start sm:gap-4">
                    <label htmlFor="endpoint_forward_email_address" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                        <p>Connector</p>
                    </label>
                    <div className="sm:col-span-3 sm:mt-0">
                        <div className="flex flex-row items-center gap-x-2 w-full">
                            <Dropdown
                                values={email_digest_connectors.map((connector) => connector.name)}
                                ids={email_digest_connectors.map((connector) => connector.uuid)}
                                selected={action.connector_uuid}
                                placeholder="Select a connector"
                                onChange={(connector_uuid) => updateConnectorUuid(connector_uuid as string)}
                            />
                            <IconButton
                                icon={PlusIcon}
                                onClick={() => setCreateEmailDigestOpen(true)}
                            />
                            <IconButton
                                icon={ArrowTopRightOnSquareIcon}
                                disabled={email_digest_connectors.find((connector) => connector.uuid === action.connector_uuid) === undefined}
                                onClick={() => { redirectToExternalPage(`/connector/${action.connector_uuid}`, true); }}
                            />
                        </div>
                    </div>
                </div>

                <EndpointCustomOutputSheets
                    prefix="email_digest"
                    in_a_box={false}
                    contexts={contexts}
                    custom_output_sheets={action.custom_output_sheets}
                    split_by_field={action.split_by_field}
                    is_committing={false}
                    updateOutputSheets={updateCustomOutputSheets}
                />
            </div>

            <div className="flex flex-row justify-end max-w-4xl">
                <Button text="Close" highlight={true} onClick={handleClose} />
            </div>
        </div>

        <TextboxModal
            icon={TbCalendar}
            open={createEmailDigestOpen}
            title="Create Email Digest Connector"
            message="Connector Name"
            cancel="Cancel"
            confirm="Create"
            validate={(name) => {
                if (name.trim() === "") { return false; }
                return true;
            }}
            onClose={createEmailDigestConnector}
        />

    </SidePanel>;
}

type EditActionEndpointProps = {
    contexts: IContextBase[];
    endpoints: IEndpointBase[];
    action: IEndpointActionEndpoint;
    onClose(updated_action?: IEndpointActionEndpoint): void;
}

function EditActionEndpoint({ contexts, endpoints, action: init_action, onClose }: EditActionEndpointProps) {
    const user = useSelector(selectUser);
    const is_admin = user.role === USER_ROLES.admin;

    const [is_changed, setIsChanged] = useState<boolean>(false);
    const [action, setAction] = useState<IEndpointActionEndpoint>(init_action);
    const [target_endpoint_template, setTargetEndpointTemplate] = useState<ITemplate | undefined>(undefined);
    const [loading_target_endpoint_template, setLoadingTargetEndpointTemplate] = useState(false);

    const has_classifier = useMemo(() => contexts.find(ctx => ctx.type === "classifier") !== undefined, [contexts]);
    const source_object_fields = useMemo(() => getObjectFields(contexts), [contexts]);
    const target_object_fields = useMemo(() => getObjectFields(target_endpoint_template?.contexts ?? []), [target_endpoint_template]);

    useEffect(() => {
        const target_endpoint = endpoints.find(endpoint => endpoint.uuid === action.endpoint_uuid);
        if (target_endpoint) {
            setLoadingTargetEndpointTemplate(true);
            BackendObj.extractions
                .getTemplate({ template_uuid: target_endpoint.template_uuid })
                .then(({ template }) => setTargetEndpointTemplate(template))
                .finally(() => setLoadingTargetEndpointTemplate(false));
        } else {
            setTargetEndpointTemplate(undefined);
            setLoadingTargetEndpointTemplate(false);
        }
    }, [action.endpoint_uuid, endpoints]);

    const handleClose = () => {
        onClose(is_changed ? action : undefined);
    }

    const updateStatus = (status: EndpointActionStatus) => {
        setAction((prev_action) => ({ ...prev_action, status }));
        setIsChanged(true);
    }

    const updateExecOn = (exec_on: EndpointActionExecOn) => {
        setAction((prev_action) => ({ ...prev_action, exec_on }));
        setIsChanged(true);
    }

    const updateConditions = (conditions: IEndpointActionCondition[]) => {
        setAction((prev_action) => ({ ...prev_action, conditions }));
        setIsChanged(true);
    }

    const updateEndpointUuid = (endpoint_uuid: string) => {
        if (endpoint_uuid === action.endpoint_uuid) { return; }
        setAction((prev_action) => ({
            ...prev_action,
            endpoint_uuid,
            field_overrides: action.field_overrides?.map(override => ({ ...override, target_field_uuid: "" }))
        }));
        setIsChanged(true);
    }

    const updateMaxChainLength = (max_chain_length: number) => {
        setAction((prev_action) => ({ ...prev_action, max_chain_length: Math.max(max_chain_length, 0) }));
        setIsChanged(true);
    }

    const updateMaxTargetRevisit = (max_target_revisit: number) => {
        setAction((prev_action) => ({ ...prev_action, max_target_revisit: Math.max(max_target_revisit, 0) }));
        setIsChanged(true);
    }

    const updatePageFilters = (page_filters: IContextInputPageFilters) => {
        setAction((prev_action) => ({ ...prev_action, page_filters }));
        setIsChanged(true);
    }

    const updateFieldOverrides = (override_idx: number, key: "source_field_uuid" | "target_field_uuid", value: string) => {
        const new_overrides = [...(action.field_overrides || [])];
        new_overrides[override_idx][key] = value;
        setAction((prev_action) => ({ ...prev_action, field_overrides: new_overrides }));
        setIsChanged(true);
    }

    const addFieldOverride = () => {
        setAction((prev_action) => ({ ...prev_action, field_overrides: [...(prev_action.field_overrides || []), { source_field_uuid: "", target_field_uuid: "" }] }));
        setIsChanged(true);
    }

    const removeFieldOverride = (override_idx: number) => {
        const new_overrides = [...(action.field_overrides || [])];
        new_overrides.splice(override_idx, 1);
        setAction((prev_action) => ({ ...prev_action, field_overrides: new_overrides }));
        setIsChanged(true);
    }

    return <SidePanel title="Action: Trigger workflow" open={true} size="2xl" onClose={handleClose}>
        <div className="py-4 flex flex-col gap-y-8">
            <ActionHeader
                contexts={contexts}
                status={action.status}
                setStatus={updateStatus}
                exec_on={action.exec_on}
                setExecOn={updateExecOn}
                conditions={action.conditions}
                setConditions={updateConditions}
            />

            <div className="flex flex-col gap-y-8 border-b border-gray-900/10 pb-8">
                <div className="sm:grid sm:grid-cols-5 sm:items-start sm:gap-4">
                    <label htmlFor="endpoint_uuid" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                        <p>Endpoint</p>
                    </label>
                    <div className="sm:col-span-3 sm:mt-0">
                        <div className="flex flex-row items-center gap-x-2 w-full">
                            <Dropdown
                                values={endpoints.map((endpoint) => endpoint.name)}
                                ids={endpoints.map((endpoint) => endpoint.uuid)}
                                selected={action.endpoint_uuid}
                                placeholder="Select a workflow"
                                onChange={(endpoint_uuid) => updateEndpointUuid(endpoint_uuid)}
                            />
                            <IconButton
                                icon={ArrowTopRightOnSquareIcon}
                                disabled={endpoints.find((endpoint) => endpoint.uuid === action.endpoint_uuid) === undefined}
                                onClick={() => { redirectToExternalPage(`/endpoint/${action.endpoint_uuid}`, true); }}
                            />
                        </div>
                    </div>
                </div>

                {is_admin && <div className="flex space-x-4">
                    <div className="w-1/2 space-y-2">
                        <label className="block text-sm font-medium text-gray-400">
                            Max Chain Length
                        </label>
                        <AdminIntegerInput
                            value={action.max_chain_length}
                            onChange={(value) => updateMaxChainLength(value)}
                            placeholder="Unlimited chain length"
                        />
                    </div>

                    <div className="w-1/2 space-y-2">
                        <label className="block text-sm font-medium text-gray-400">
                            Max Target Revisits
                        </label>
                        <AdminIntegerInput
                            value={action.max_target_revisit}
                            onChange={(value) => updateMaxTargetRevisit(value)}
                            placeholder="Revisits of target not limited"
                        />
                    </div>
                </div>}

                {has_classifier && <div className="space-y-2">
                    {action.page_filters && (
                        <PageFilters
                            classifiers={contexts.find(ctx => ctx.type === "classifier")?.extract_params.classifiers ?? []}
                            input_page_filters={action.page_filters}
                            onChange={(filters) => updatePageFilters(filters)}
                            hide_required_nonempty={true}
                        />
                    )}
                </div>}

                <div className="space-y-4">
                    <div className="flex flex-row items-center gap-x-2">
                        <p className="text-sm font-medium leading-6 text-gray-900">Field Overrides</p>
                        <p className="grow" />
                        <Button icon={PlusIcon} onClick={addFieldOverride} />
                    </div>

                    {(action.field_overrides?.length ?? 0) > 0 && <div className="flex flex-col gap-y-2">
                        <div className="flex space-x-4 items-center">
                            <div className="w-1/2">
                                <label className="block text-sm text-gray-900">
                                    Source Field
                                </label>
                            </div>
                            <div className="w-1/2 flex items-center">
                                <label className="block text-sm text-gray-900">
                                    Target Field
                                </label>
                            </div>
                        </div>

                        {loading_target_endpoint_template && <div className="flex items-center">
                            <LoadingSpinnerLimit />
                        </div>}

                        {!loading_target_endpoint_template && action.field_overrides?.map((override, override_idx) => (
                            <div key={override_idx} className="flex space-x-2 items-center">
                                <div className="w-1/2 flex flex-row items-center gap-x-2">
                                    <Dropdown
                                        values={source_object_fields.map(f => f.display_name)}
                                        ids={source_object_fields.map(f => f.uuid)}
                                        selected={override.source_field_uuid}
                                        onChange={(value) => updateFieldOverrides(override_idx, "source_field_uuid", value)}
                                        placeholder="Select a source field"
                                    />
                                    <ArrowRightIcon className="h-5 w-5" />
                                </div>
                                <div className="w-1/2 flex items-center gap-x-2">
                                    <Dropdown
                                        values={target_object_fields.map(f => f.display_name)}
                                        ids={target_object_fields.map(f => f.uuid)}
                                        selected={override.target_field_uuid}
                                        onChange={(value) => updateFieldOverrides(override_idx, "target_field_uuid", value)}
                                        placeholder="Select a target field"
                                        disabled={target_endpoint_template === undefined}
                                    />
                                    <IconButton
                                        icon={XMarkIcon}
                                        onClick={() => removeFieldOverride(override_idx)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>}
                </div>
            </div>

            <div className="flex flex-row justify-end max-w-4xl">
                <Button text="Close" highlight={true} onClick={handleClose} />
            </div>
        </div>

    </SidePanel>;
}


type EditActionWebhookProps = {
    template: ITemplateBase;
    contexts: IContextBase[];
    connectors: IConnector[];
    action: IEndpointActionWebhook;
    refreshConnectors(): void;
    onClose(updated_action?: IEndpointActionWebhook): void;
}

function EditActionWebhook({ template, contexts, connectors, action: init_action, refreshConnectors, onClose }: EditActionWebhookProps) {
    const user = useSelector(selectUser);

    const [is_changed, setIsChanged] = useState<boolean>(false);
    const [action, setAction] = useState<IEndpointActionWebhook>(init_action);
    const is_custom_reply_format_enabled = user.role === USER_ROLES.admin || user.role === USER_ROLES.enterprise;
    const [is_custom_format_not_available_open, setIsCustomFormatNotAvailableOpen] = useState<boolean>(false);
    const [webhook_attachment_custom_output_idx, setWebhookAttachmentCustomOutputIdx] = useState<number | undefined>(undefined);
    const [webhook_attachment_editor_idx, setWebhookAttachmentEditorIdx] = useState<number | undefined>(undefined);
    const [create_webhook_open, setCreateWebhookOpen] = useState<boolean>(false);

    const webhook_connectors = useMemo<IConnector[]>(
        () => connectors.filter((connector) => connector.type === "webhook"),
        [connectors]);

    const handleClose = () => {
        onClose(is_changed ? action : undefined);
    }

    const updateStatus = (status: EndpointActionStatus) => {
        setAction((prev_action) => ({ ...prev_action, status }));
        setIsChanged(true);
    }

    const updateExecOn = (exec_on: EndpointActionExecOn) => {
        setAction((prev_action) => ({ ...prev_action, exec_on }));
        setIsChanged(true);
    }

    const updateConditions = (conditions: IEndpointActionCondition[]) => {
        setAction((prev_action) => ({ ...prev_action, conditions }));
        setIsChanged(true);
    }

    const updateConnectorUuid = (connector_uuid: string) => {
        setAction((prev_action) => ({ ...prev_action, connector_uuid }));
        setIsChanged(true);
    }

    const updateSplitByField = (split_by_field: IEndpointSplitByField[] | undefined) => {
        setAction((prev_action) => ({ ...prev_action, split_by_field }));
        setIsChanged(true);
    }

    const updateItemAggregation = (item_aggregation: EndpointItemAggregation) => {
        setAction((prev_action) => ({ ...prev_action, item_aggregation }));
        setIsChanged(true);
    }

    const updateCustomOutputSheets = (custom_output_sheets: IScrapesRenderConfig[]) => {
        setAction((prev_action) => ({ ...prev_action, custom_output_sheets }));
        setIsChanged(true);
    }

    const deleteWebhookAttachment = (idx: number) => {
        setAction((prev_action) => ({ ...prev_action, attachments: prev_action.attachments?.filter((_, index) => index !== idx) }));
        setIsChanged(true);
    }

    const addWebhookAttachment = () => {
        setAction((prev_action) => ({ ...prev_action, attachments: [...(prev_action.attachments || []), deepCopy(INIT_ATTACHMENTS[0])] }));
        setIsChanged(true);
    }

    const updateWebhookAttachmentFileFormat = (idx: number, format: EndpointAttachmentFileFormat) => {
        if ((format === "custom" || format === "custom_javascript") && !is_custom_reply_format_enabled) {
            setIsCustomFormatNotAvailableOpen(true);
        } else {
            setAction((prev_action) => ({
                ...prev_action, attachments: prev_action.attachments?.map(
                    (attachment, index) => index === idx ? { ...attachment, file_format: format } : attachment)
            }));
            setIsChanged(true);
        }
    }

    const updateWebhookAttachmentCustomFileName = (idx: number, custom_file_name: IEndpointOutputNameElement[]) => {
        setAction((prev_action) => ({
            ...prev_action, attachments: prev_action.attachments?.map(
                (attachment, index) => index === idx ? { ...attachment, custom_file_name } : attachment)
        }));
        setIsChanged(true);
    }

    const updateWebhookAttachmentCustomOutputSheets = (idx: number, custom_output_sheets: IScrapesRenderConfig[]) => {
        setAction((prev_action) => ({
            ...prev_action, attachments: prev_action.attachments?.map(
                (attachment, index) => index === idx ? { ...attachment, custom_output_sheets } : attachment)
        }));
        setIsChanged(true);
    }

    const updateWebhookAttachmentSplitByField = (idx: number, split_by_field: IEndpointSplitByField[] | undefined) => {
        setAction((prev_action) => ({
            ...prev_action, attachments: prev_action.attachments?.map(
                (attachment, index) => index === idx ? { ...attachment, split_by_field } : attachment)
        }));
        setIsChanged(true);
    }

    const updateWebhookAttachmentHandlebarDetails = (idx: number, handlebar_details: IHandlebarDetails) => {
        setAction((prev_action) => ({
            ...prev_action, attachments: prev_action.attachments?.map(
                (attachment, index) => index === idx ? {
                    ...attachment,
                    handlebar_template: handlebar_details.handlebar_template,
                    handlebar_file_extension: handlebar_details.handlebar_file_extension,
                    handlebar_file_mimetype: handlebar_details.handlebar_file_mimetype
                } : attachment)
        }));
        setWebhookAttachmentEditorIdx(undefined);
        setIsChanged(true);
    }

    const updateWebhookAttachmentJavascriptDetails = (idx: number, javascript_details: ICustomJavascriptDetails) => {
        setAction((prev_action) => ({
            ...prev_action, attachments: prev_action.attachments?.map(
                (attachment, index) => index === idx ? {
                    ...attachment,
                    custom_javascript_file_format: javascript_details.custom_javascript_file_format,
                    custom_javascript_formula: javascript_details.custom_javascript_formula
                } : attachment)
        }));
        setWebhookAttachmentEditorIdx(undefined);
        setIsChanged(true);
    }

    const updateIncludeInput = (include_input: EndpointWebhookIncludeInput) => {
        setAction((prev_action) => ({ ...prev_action, include_input }));
        setIsChanged(true);
    }

    const createWebhookConnector = async (result: boolean, name?: string, url?: string) => {
        setCreateWebhookOpen(false);
        if (result && name !== undefined && url !== undefined) {
            const new_connector_details = getInitConnectorDetails("webhook") as IConnectorWebhookDetails;
            new_connector_details.webhook_url = url;
            const { connector_uuid } = await BackendObj.extractions.createConnector({
                name: name,
                type: "webhook",
                org_uuid: template.org_uuid,
                details: new_connector_details
            });
            updateConnectorUuid(connector_uuid);
            refreshConnectors();
        }
    }

    return <SidePanel title="Action: Call webhook" open={true} size="2xl" onClose={handleClose}>
        <div className="py-4 flex flex-col gap-y-8">
            <ActionHeader
                contexts={contexts}
                status={action.status}
                setStatus={updateStatus}
                exec_on={action.exec_on}
                setExecOn={updateExecOn}
                conditions={action.conditions}
                setConditions={updateConditions}
            />

            <div className="sm:grid sm:grid-cols-5 sm:items-start sm:gap-4">
                <label htmlFor="endpoint_forward_email_address" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                    <p>Connector</p>
                </label>
                <div className="sm:col-span-3 sm:mt-0">
                    <div className="flex flex-row items-center gap-x-2 w-full">
                        <Dropdown
                            values={webhook_connectors.map((connector) => connector.name)}
                            ids={webhook_connectors.map((connector) => connector.uuid)}
                            selected={action.connector_uuid}
                            placeholder="Select a connector"
                            onChange={(connector_uuid) => updateConnectorUuid(connector_uuid as string)}
                        />
                        <IconButton
                            icon={PlusIcon}
                            onClick={() => setCreateWebhookOpen(true)}
                        />
                        <IconButton
                            icon={ArrowTopRightOnSquareIcon}
                            disabled={webhook_connectors.find((connector) => connector.uuid === action.connector_uuid) === undefined}
                            onClick={() => { redirectToExternalPage(`/connector/${action.connector_uuid}`, true); }}
                        />
                    </div>
                </div>
            </div>


            <div className="sm:grid sm:grid-cols-5 sm:items-start sm:gap-4">
                <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                    <p>Aggregation</p>
                </label>
                <div className="mt-2 sm:mt-0 w-full sm:col-span-3">
                    <Dropdown
                        values={["Per job", "Per item"]}
                        ids={["per_job", "per_item"]}
                        selected={action.item_aggregation}
                        onChange={(item_aggregation) => updateItemAggregation(item_aggregation as EndpointItemAggregation)} />
                </div>
            </div>

            <div className="sm:grid sm:grid-cols-5 sm:items-start sm:gap-4">
                <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                    <p>Include from input</p>
                </label>
                <div className="mt-2 sm:mt-0 col-span-3">
                    <Dropdown
                        values={["Nothing", "Attachments only", "First attachment only", "Email body and all attachments"]}
                        ids={["no", "attachments", "main_attachment", "all"]}
                        selected={action.include_input}
                        onChange={(include_input) => updateIncludeInput(include_input as EndpointWebhookIncludeInput)} />
                </div>
            </div>

            <EndpointSplitByField
                prefix="webhook"
                in_a_box={false}
                contexts={contexts}
                split_by_field={action.split_by_field}
                is_committing={false}
                tooltip="You can split the result into multiple individual webhook calls by selecting one or more
                        split fields. A separate webhook call is created for each unique combination of the split fields.
                        Steps for which no split fields are selected are included in all the webhook calls."
                updateSplitByField={(split_by_field) => updateSplitByField(split_by_field)} />

            <EndpointCustomOutputSheets
                prefix="webhook"
                in_a_box={false}
                contexts={contexts}
                custom_output_sheets={action.custom_output_sheets}
                split_by_field={action.split_by_field}
                is_committing={false}
                tooltip="By default all the fields from the template are included in the webhook call.
                        If you want to customize the output, you can do it here. Just list the sheets and columns you
                        want to include in the webhook call. Sheets and columns will correspond to keys in the JSON response.
                        Fields with note 'multiple values possible' can have multiple values, and only the first value will
                        be used for the output."
                updateOutputSheets={(output_sheets) => updateCustomOutputSheets(output_sheets)}
            />
        </div>

        <div className="flex flex-col gap-y-8">
            <div className="flex flex-col gap-4 pb-6 border-b border-gray-900/10">
                <div className="sm:col-span-6 flex flex-row items-center gap-x-2">
                    <p className="text-sm font-medium leading-6 sm:pt-1.5 text-gray-900">Webhook attachments</p>
                    <div className="flex-grow" />
                    <Button icon={PlusIcon} onClick={addWebhookAttachment} />
                </div>
                {action.attachments?.map((attachment, idx) => (
                    <div key={idx} className="sm:grid sm:grid-cols-6 sm:gap-4 p-4 border border-gray-900/10 bg-gray-50 rounded-lg">
                        <div className="sm:col-span-6 flex flex-row items-center gap-x-2">
                            <p className="text-sm font-medium leading-6 sm:pt-1.5 text-gray-900">Attachment {idx + 1}</p>
                            {attachment.custom_output_sheets.length > 0 && <p className="pt-1 text-xs text-gray-400">custom template</p>}
                            {attachment.custom_file_name.length > 0 && <p className="pt-1 text-xs text-gray-400">custom filename</p>}
                            {(attachment.split_by_field?.length ?? 0) > 0 && <p className="pt-1 text-xs text-gray-400">split enabled</p>}
                            <div className="flex-grow" />
                            <Button text={"Edit Format"} onClick={() => setWebhookAttachmentCustomOutputIdx(idx)} />
                            {attachment.file_format === "custom" && <Button text="Edit Handlebars" onClick={() => setWebhookAttachmentEditorIdx(idx)} />}
                            {attachment.file_format === "custom_javascript" && <Button text="Edit JavaScript" onClick={() => setWebhookAttachmentEditorIdx(idx)} />}
                            <IconButton icon={XMarkIcon} onClick={() => deleteWebhookAttachment(idx)} />
                        </div>
                        <label htmlFor={`endpoint_webhook_attachment_${idx}`}
                            className="block sm:col-span-3 text-sm font-medium leading-6 sm:pt-1.5 text-gray-900">
                            <p>Webhook attachment format</p>
                        </label>
                        <div className="mt-2 sm:mt-0 sm:col-span-3">
                            <Dropdown
                                values={["Excel", "CSV (comma)", "CSV (semicolon)", "TSV", "PDF", "Prepend PDF to attachment", "Append PDF to attachment", "JSON", "Custom - Handlebars", "Custom - JavaScript"]}
                                ids={["excel", "csv", "csv-semicolon", "tsv", "pdf", "prepend_pdf", "append_pdf", "json", "custom", "custom_javascript"]}
                                selected={attachment.file_format}
                                onChange={(format) => updateWebhookAttachmentFileFormat(idx, format as EndpointAttachmentFileFormat)} />
                        </div>
                    </div>
                ))}
                {webhook_attachment_custom_output_idx !== undefined && <Fragment>
                    <EndpointCustomOutput
                        title="Attachment"
                        contexts={contexts}
                        custom_file_name={action.attachments?.[webhook_attachment_custom_output_idx]?.custom_file_name}
                        custom_output_sheets={["custom", "custom_javascript"].includes(action.attachments?.[webhook_attachment_custom_output_idx]?.file_format) ?
                            undefined : action.attachments?.[webhook_attachment_custom_output_idx]?.custom_output_sheets}
                        split_by_field={action.attachments?.[webhook_attachment_custom_output_idx]?.split_by_field}
                        is_committing={false}
                        updateCustomFileName={(custom_file_name) => updateWebhookAttachmentCustomFileName(webhook_attachment_custom_output_idx, custom_file_name)}
                        updateOutputSheets={["custom", "custom_javascript"].includes(action.attachments?.[webhook_attachment_custom_output_idx]?.file_format) ?
                            undefined : (custom_output_sheets) => updateWebhookAttachmentCustomOutputSheets(webhook_attachment_custom_output_idx, custom_output_sheets)}
                        updateSplitByField={(split_by_field) => updateWebhookAttachmentSplitByField(webhook_attachment_custom_output_idx, split_by_field)}
                        onClose={() => setWebhookAttachmentCustomOutputIdx(undefined)}
                    />
                </Fragment>}
                {template && webhook_attachment_editor_idx !== undefined && <Fragment>
                    {action.attachments?.[webhook_attachment_editor_idx]?.file_format === "custom" && <AttachmentHandlebarDetails
                        template_uuid={template?.uuid}
                        split_by_field={action.attachments?.[webhook_attachment_editor_idx]?.split_by_field}
                        handlebar_template={action.attachments?.[webhook_attachment_editor_idx]?.handlebar_template}
                        handlebar_file_extension={action.attachments?.[webhook_attachment_editor_idx]?.handlebar_file_extension}
                        handlebar_file_mimetype={action.attachments?.[webhook_attachment_editor_idx]?.handlebar_file_mimetype}
                        onClose={(handlebar_details) => updateWebhookAttachmentHandlebarDetails(webhook_attachment_editor_idx, handlebar_details)}
                    />}
                    {action.attachments?.[webhook_attachment_editor_idx]?.file_format === "custom_javascript" && <AttachmentJavascriptDetails
                        template_uuid={template?.uuid}
                        contexts={contexts}
                        split_by_field={action.attachments?.[webhook_attachment_editor_idx]?.split_by_field}
                        custom_javascript_file_format={action.attachments?.[webhook_attachment_editor_idx]?.custom_javascript_file_format}
                        custom_javascript_formula={action.attachments?.[webhook_attachment_editor_idx]?.custom_javascript_formula}
                        onClose={(javascript_details) => updateWebhookAttachmentJavascriptDetails(webhook_attachment_editor_idx, javascript_details)}
                    />}
                </Fragment>}
            </div>

            <div className="flex flex-row justify-end max-w-4xl">
                <Button text="Close" highlight={true} onClick={handleClose} />
            </div>
        </div>

        <WarningModal
            title="Feature not available"
            open={is_custom_format_not_available_open}
            onClose={() => setIsCustomFormatNotAvailableOpen(false)}>
            <div className="flex flex-col text-sm text-gray-900 gap-y-4 py-4">
                <p>Custom attachment format is only enabled for our enterprise customers.</p>
                <p>Please <a href="https://meetings-eu1.hubspot.com/tomaz/book-a-demo" className="text-space_blue-600" target="_blank" rel="noreferrer">contact our team</a> for more information on how to upgrade.</p>
            </div>
        </WarningModal>

        <TwoTextboxModal
            icon={ShareIcon}
            open={create_webhook_open}
            title="Create new webhook connector"
            message1="Connector Name"
            message2="Webhook URL"
            cancel="Cancel"
            confirm="Create"
            validate={(name, url) => {
                if (name.trim() === "") { return false; }
                if (url.trim() === "") { return false; }
                return true;
            }}
            onClose={createWebhookConnector}
        />
    </SidePanel >;

}


type EditActionTableUpdateProps = {
    contexts: IContextBase[];
    lookup_tables: ILookupTableBase[];
    action: IEndpointActionTableUpdate;
    onClose(updated_action?: IEndpointActionTableUpdate): void;
}

function EditActionTableUpdate({ contexts, lookup_tables, action: init_action, onClose }: EditActionTableUpdateProps) {
    const [is_changed, setIsChanged] = useState<boolean>(false);
    const [action, setAction] = useState<IEndpointActionTableUpdate>(init_action);

    const selected_lookup_table = useMemo<ILookupTableBase | undefined>(
        () => lookup_tables.find((table) => table.uuid === action.lookup_table_uuid),
        [lookup_tables, action.lookup_table_uuid]);
    const selected_context = useMemo<IContextBase | undefined>(
        () => contexts.find((context) => context.uuid === action.mapping.context_uuid),
        [contexts, action.mapping.context_uuid]);

    const columns = useMemo<(ITableUpdateColumnConfig & { name?: string })[]>(
        () => action.mapping.columns.map(c => ({
            ...c,
            name: selected_lookup_table?.headers.find(h => h.uuid === c.header_uuid)?.name ?? "",
        })),
        [action.mapping.columns, selected_lookup_table]);

    useEffect(() => {
        const refreshColumns = async (): Promise<void> => {
            if (selected_lookup_table === undefined) { return; }
            // Create a map of existing columns to preserve values where possible
            const existing_columns_map = new Map<string, (ITableUpdateColumnConfig & { name?: string })>();
            for (const col of columns) {
                existing_columns_map.set(col.header_uuid, col);
            }
            // check if there is any column difference that requires a refresh
            const has_new_columns = selected_lookup_table.headers.some(header => !existing_columns_map.has(header.uuid));
            const has_removed_columns = columns.some(col => !selected_lookup_table.headers.some(header => header.uuid === col.header_uuid));
            if (has_new_columns || has_removed_columns) {
                // Create new columns array based on current headers
                const new_columns = selected_lookup_table?.headers.map(header => {
                    return existing_columns_map.get(header.uuid) || {
                        header_uuid: header.uuid,
                        type: "const" as "const" | "field",
                        const_value: ""
                    };
                });
                setAction((prev_action) => ({ ...prev_action, mapping: { ...prev_action.mapping, columns: new_columns } }));
                setIsChanged(true);
            }
        };
        refreshColumns().catch(console.error);
    }, [selected_lookup_table, columns]);

    const handleClose = () => {
        onClose(is_changed ? action : undefined);
    }

    const updateStatus = (status: EndpointActionStatus) => {
        setAction((prev_action) => ({ ...prev_action, status }));
        setIsChanged(true);
    }

    const updateExecOn = (exec_on: EndpointActionExecOn) => {
        setAction((prev_action) => ({ ...prev_action, exec_on }));
        setIsChanged(true);
    }

    const updateConditions = (conditions: IEndpointActionCondition[]) => {
        setAction((prev_action) => ({ ...prev_action, conditions }));
        setIsChanged(true);
    }

    const updateLookupTableUuid = (lookup_table_uuid: string) => {
        setAction((prev_action) => ({ ...prev_action, lookup_table_uuid }));
        setIsChanged(true);
    }

    const updateContextUuid = (context_uuid: string) => {
        setAction((prev_action) => ({ ...prev_action, mapping: { ...prev_action.mapping, context_uuid } }));
        setIsChanged(true);
    }

    const updateUpdateType = (update_type: TableUpdateActionType) => {
        setAction((prev_action) => ({ ...prev_action, update_type }));
        // in case we switch to merge, we need to make sure at least one column is a hash key
        if (update_type === "merge_incoming_wins" || update_type === "merge_existing_wins") {
            const has_hash_key = columns.some(c => c.hash_key);
            if (!has_hash_key) {
                // no hash key, make first column a hash key
                const new_columns = [...columns];
                new_columns[0].hash_key = true;
                setAction((prev_action) => ({ ...prev_action, mapping: { ...prev_action.mapping, columns: new_columns } }));
            }
        }
        setIsChanged(true);
    }

    const setColumn = (column_idx: number, column: IScrapesRenderColumnConfig & { header_uuid?: string }) => {
        const new_columns = [...action.mapping.columns];
        if (column.header_uuid === undefined) { return; }
        new_columns[column_idx] = {
            header_uuid: column.header_uuid,
            type: column.type,
            const_value: column.const_value,
            field: column.field,
            row_number_type: column.row_number_type,
            hash_key: column.hash_key
        };
        setAction((prev_action) => ({ ...prev_action, mapping: { ...prev_action.mapping, columns: new_columns } }));
        setIsChanged(true);
    };

    return <SidePanel title="Action: Update table" open={true} size="2xl" onClose={handleClose}>
        <div className="py-4 flex flex-col gap-y-8">
            <ActionHeader
                contexts={contexts}
                status={action.status}
                setStatus={updateStatus}
                exec_on={action.exec_on}
                setExecOn={updateExecOn}
                conditions={action.conditions}
                setConditions={updateConditions}
            />

            <div className="flex flex-col gap-y-4 border-b border-gray-900/10 pb-4">
                <div className="pb-4 sm:grid sm:grid-cols-5 sm:items-start sm:gap-4">
                    <label htmlFor="endpoint_table_update_table" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                        <p>Table</p>
                    </label>
                    <div className="sm:col-span-3 sm:mt-0">
                        <div className="flex flex-row items-center gap-x-2 w-full">
                            <Dropdown
                                values={lookup_tables.map((table) => table.name)}
                                ids={lookup_tables.map((table) => table.uuid)}
                                selected={action.lookup_table_uuid}
                                placeholder="Select a table"
                                onChange={(lookup_table_uuid) => updateLookupTableUuid(lookup_table_uuid as string)}
                            />
                            <IconButton
                                icon={ArrowTopRightOnSquareIcon}
                                disabled={lookup_tables.find((table) => table.uuid === action.lookup_table_uuid) === undefined}
                                onClick={() => { redirectToExternalPage(`/lookup_table/${action.lookup_table_uuid}`, true); }}
                            />
                        </div>
                    </div>
                    <label htmlFor="endpoint_table_update_step" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                        <p>Step</p>
                    </label>
                    <div className="sm:col-span-3 sm:mt-0">
                        <div className="flex flex-row items-center gap-x-2 w-full">
                            <Dropdown
                                values={contexts.map((context, idx) => context.name === "" ? `Step ${idx + 1}` : context.name)}
                                ids={contexts.map((context) => context.uuid)}
                                selected={action.mapping.context_uuid}
                                placeholder="Select a step"
                                onChange={(context_uuid) => updateContextUuid(context_uuid as string)}
                            />
                        </div>
                    </div>
                    <label htmlFor="endpoint_table_update_step" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-2">
                        <p>Update Type</p>
                    </label>
                    <div className="sm:col-span-3 sm:mt-0">
                        <div className="flex flex-row items-center gap-x-2 w-full">
                            <Dropdown
                                values={["Append New Records", "Merge (New Records Win)", "Merge (Existing Records Win)"]}
                                ids={["append", "merge_incoming_wins", "merge_existing_wins"]}
                                selected={action.update_type}
                                placeholder="Select a update type"
                                onChange={(update_type) => updateUpdateType(update_type as TableUpdateActionType)}
                            />
                        </div>
                    </div>
                </div>

                {selected_context && <EndpointOutputColumns
                    context={selected_context}
                    other_contexts={contexts
                        ?.map((context, context_idx) => ({ ...context, step_idx: context_idx }))
                        .filter(c => c.type === "object" && c.uuid !== selected_context.uuid) ?? []}
                    endpoint_split_by_field={[]}
                    columns={action.mapping.columns.map(c => ({
                        header_uuid: c.header_uuid,
                        name: selected_lookup_table?.headers.find(h => h.uuid === c.header_uuid)?.name ?? "",
                        type: c.type,
                        const_value: c.const_value,
                        field: c.field,
                        row_number_type: c.row_number_type,
                        hash_key: c.hash_key
                    }))}
                    show_hash_key={action.update_type === "merge_incoming_wins" || action.update_type === "merge_existing_wins"}
                    allow_zero_hash_keys={action.update_type === "append"}
                    setColumn={setColumn}
                />}

                {<div className="text-sm text-gray-600 mt-2 max-w-2xl">
                    {action.update_type === "append" && "All rows will be added to the table."}
                    {action.update_type === "merge_incoming_wins" && <span>Incoming rows will overwrite existing ones based on matching <span className="font-semibold">[{columns.filter(c => c.hash_key).map(c => c.name).join(", ")}]</span> {columns.filter(c => c.hash_key).length === 1 ? "value" : "values"}.</span>}
                    {action.update_type === "merge_existing_wins" && <span>Only incoming rows with new {columns.filter(c => c.hash_key).length === 1 ? "value for " : "combinations of values for "}<span className="font-semibold">[{columns.filter(c => c.hash_key).map(c => c.name).join(", ")}]</span> will be added to the table.</span>}
                </div>}
            </div>

            <div className="flex flex-row justify-end max-w-4xl">
                <Button text="Close" highlight={true} onClick={handleClose} />
            </div>
        </div>
    </SidePanel>;
}